import { EntityKind, type EntityState } from "odyssey-shared";
import type { DrawnCharacter } from "../remote-characters.ts";
import type { CompanionFollowers } from "./followers.ts";

/**
 * What the page's own character offers the companions: where it is drawn, which way it faces and whether it runs.
 */
export interface OwnCompanionOwner {
  readonly drawn: { readonly x: number; readonly y: number };
  facing(): number;
  readonly running: boolean;
}

/**
 * Tells the companions every player in view who has one out, the page's own character first, and poses them (ADR-142
 * decision 7.2). A player's companion is read from the Companion component of the view the page holds for them, so the
 * owner and every watcher see the same, and he is drawn from where the player is drawn this frame. The visit of the
 * players in view is made once, so a frame allocates nothing for it.
 */
export class CompanionFeed {
  private entities: ReadonlyMap<number, EntityState> = new Map();
  private deltaMs = 0;

  /**
   * @param followers The companions drawn.
   */
  public constructor(private readonly followers: CompanionFollowers) {}

  /**
   * Feeds one frame.
   *
   * @param entities The client's view.
   * @param localHandle The page's own character's handle.
   * @param local The page's own character.
   * @param others The other characters and creatures drawn, by handle.
   * @param deltaMs The frame's length in ms.
   * @param nowMs Scene time in ms.
   * @param ownName The page's own companion's name, empty when the server has not told it.
   * @param pointerX The pointer's world point across.
   * @param pointerY The same down.
   */
  public frame(entities: ReadonlyMap<number, EntityState>, localHandle: number, local: OwnCompanionOwner, others: ReadonlyMap<number, DrawnCharacter>, deltaMs: number, nowMs: number, ownName: string, pointerX: number, pointerY: number): void {
    this.entities = entities;
    this.deltaMs = deltaMs;
    this.followers.begin();
    const me = entities.get(localHandle);
    if (me !== undefined) {
      this.followers.owner(localHandle, me.companion ?? 0, true, local.drawn.x, local.drawn.y, local.facing(), local.running, deltaMs, ownName);
    }
    others.forEach(this.visit);
    this.followers.finish(nowMs, pointerX, pointerY);
  }

  private readonly visit = (drawn: DrawnCharacter, handle: number): void => {
    if (drawn.kind !== EntityKind.Player) {
      return;
    }
    const state = this.entities.get(handle);
    if (state !== undefined && (state.companion ?? 0) !== 0) {
      this.followers.owner(handle, state.companion ?? 0, false, drawn.drawnX, drawn.drawnY, drawn.facing, state.running === true, this.deltaMs, state.name.first);
    }
  };
}
